/**
 * Which significance marks a reader draws, and how (M4S-5777, defect 7 / X71, X75). Spec:
 * docs/pdlc/crosstabs-significance-full.md §2.3 and §6.
 *
 * One answer for the table, the chart and every file, so the views cannot disagree again: before
 * this, Letters + "vs Total" drew nothing in the table while the chart drew ▲ / ▼.
 *
 * - **Letters** are drawn in Letters style when the scope compares columns with each other
 *   (`group`, `all`).
 * - **The vs-Total arrows** (`Cell.vsTotal`) are drawn in Arrows style, and in Letters style when
 *   the scope is `total`, where they are the only mark (decision S1: no letter `T`).
 * - **The chart** has no room for letters, so it draws the vs-Total arrows whenever significance is
 *   on, as it always has. In `total` scope that is exactly what the table draws.
 */

export interface SigMarks {
    letters: boolean;
    totalArrows: boolean;
}

export function sigMarksOf(sigStyle: unknown, sigScope: unknown): SigMarks {
    const vsTotal = sigScope === 'total';
    return {
        letters: sigStyle === 'letters' && !vsTotal,
        totalArrows: sigStyle === 'arrows' || (sigStyle === 'letters' && vsTotal),
    };
}

/** The chart's rule: the vs-Total arrows whenever significance is shown at all. */
export function chartShowsTotalArrows(sigStyle: unknown): boolean {
    return sigStyle === 'letters' || sigStyle === 'arrows';
}

export const TOTAL_ARROWS = { up: '▲', down: '▼' } as const;

/** The arrow a cell prints, or '' for none. */
export function totalArrowOf(vsTotal: 'up' | 'down' | null | undefined): string {
    return vsTotal ? TOTAL_ARROWS[vsTotal] : '';
}

// ── Chi-square (X75) ──────────────────────────────────────────────────────────

/** Why a banner group of a table has no chi-square. */
export type ChiSquareSkip =
    | 'multi-answer-question'
    | 'overlapping-columns'
    | 'too-few-columns'
    | 'too-few-answers'
    | 'small-expected-counts';

export const CHI_SQUARE_SKIP_TEXT: Record<ChiSquareSkip, string> = {
    'multi-answer-question': 'not tested: respondents could give more than one answer',
    'overlapping-columns': 'not tested: the columns share respondents',
    'too-few-columns': 'not tested: fewer than two columns with a base of 30',
    'too-few-answers': 'not tested: fewer than two answers',
    'small-expected-counts': 'not tested: expected counts under 1',
};

export interface ChiSquareResult {
    p: number;
    df: number;
    statistic: number;
}

/** "0.012", or "< 0.001". */
export function formatP(p: number): string {
    return p < 0.001 ? '< 0.001' : `= ${p.toFixed(3)}`;
}

/**
 * One line per banner group for a table that ran χ²: "Gender: p = 0.012 (χ² 8.40, df 2)" or
 * "Region: not tested: the columns share respondents". Empty when χ² was not chosen.
 */
export function chiSquareLines(block: {
    chiSquare?: Record<string, ChiSquareResult>;
    chiSquareSkipped?: Record<string, ChiSquareSkip>;
}): string[] {
    const lines: string[] = [];
    for (const [group, r] of Object.entries(block.chiSquare ?? {})) {
        lines.push(`${group}: p ${formatP(r.p)} (χ² ${r.statistic.toFixed(2)}, df ${r.df})`);
    }
    for (const [group, why] of Object.entries(block.chiSquareSkipped ?? {})) {
        lines.push(`${group}: ${CHI_SQUARE_SKIP_TEXT[why]}`);
    }
    return lines;
}
